import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { U2000FormStateEnumState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { NS } from "../../../../U3121";
import { useU3121Context } from "../../../../U3121Context";
import type { U3121Project, U3121WorkNature, U3121WorkNatureAssignment } from "../../../../U3121Service";
import { u3121SyncTaskAssignmentsFromWorkNatures } from "../../../../tools/u3121SyncTaskAssignmentsFromWorkNatures";

interface U3121ProjectWorkNatureAssignmentBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3121Project>;
  t: TFunction;
  onValidateAfterWorkNatureChange?: (project: U3121Project) => void;
  onValidateAfterTaskChange?: (project: U3121Project) => void;
}

function getWorkNatureLabel(wn: U3121WorkNature, language: string): string {
  if (language === "fr" && wn.workNatureDescriptionFr?.trim()) return wn.workNatureDescriptionFr.trim();
  if (language === "en" && wn.workNatureDescriptionEn?.trim()) return wn.workNatureDescriptionEn.trim();
  return wn.workNatureDescription?.trim() || wn.workNatureCode?.trim() || wn.id;
}

function getSelectedWorkNatureIdsFromProject(project: U3121Project | null | undefined): Set<string> {
  const raw = project?.workNatureAssignments;
  if (!raw?.length) return new Set();
  const ids = (raw as (string | U3121WorkNatureAssignment)[]).map((item) => (typeof item === "string" ? item : item.id));
  return new Set(ids);
}

function U3121ProjectWorkNatureAssignmentBody({
  formStateHandlers,
  t,
  onValidateAfterWorkNatureChange,
  onValidateAfterTaskChange,
}: U3121ProjectWorkNatureAssignmentBodyProps) {
  const { i18n } = useTranslation();
  const { initLU } = useU3121Context();
  const workNatures = initLU.workNatures ?? [];
  const activeTaskIds = (initLU.tasks ?? []).map((task) => task.id);
  const language = i18n.language;
  const isEditMode = formStateHandlers.getFormState() !== U2000FormStateEnumState.READ;

  const project = formStateHandlers.getData();
  const selectedWorkNatureIds = getSelectedWorkNatureIdsFromProject(project);
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const workNatureIds = workNatures.map((wn) => wn.id);
  const catalogIdSet = new Set(workNatureIds);
  const orphanSelectedIds = [...selectedWorkNatureIds].filter((id) => !catalogIdSet.has(id));
  const allSelected = workNatureIds.length > 0 && workNatureIds.every((id) => selectedWorkNatureIds.has(id));
  const someSelected = selectedWorkNatureIds.size > 0 && selectedWorkNatureIds.size < workNatureIds.length;

  useEffect(() => {
    const el = selectAllRef.current;
    if (!el) return;
    el.indeterminate = someSelected;
  }, [someSelected]);

  const commitWorkNaturesAndSyncTasks = (newIds: Set<string>) => {
    const data = formStateHandlers.getData();
    if (!data) return;
    const workNatureAssignments = Array.from(newIds).map((id) => ({ id }));
    const nextProject = u3121SyncTaskAssignmentsFromWorkNatures(
      { ...data, workNatureAssignments },
      workNatures,
      activeTaskIds,
    );
    formStateHandlers.setData(nextProject);
    if (onValidateAfterWorkNatureChange) onValidateAfterWorkNatureChange(nextProject);
    if (onValidateAfterTaskChange) onValidateAfterTaskChange(nextProject);
  };

  const handleSelectAllChange = () => {
    const clearingAll = allSelected || someSelected;
    const newIds = clearingAll ? new Set<string>() : new Set(workNatureIds);
    commitWorkNaturesAndSyncTasks(newIds);
  };

  const handleWorkNatureChange = (workNatureId: string, checked: boolean) => {
    const next = new Set(selectedWorkNatureIds);
    if (checked) next.add(workNatureId);
    else next.delete(workNatureId);
    commitWorkNaturesAndSyncTasks(next);
  };

  const syncHint = isEditMode ? (
    <p className="mb-3 text-sm text-gray-600">{t("msgWorkNatureSyncTaskAssignmentsHint", { ns: NS })}</p>
  ) : null;

  if (!isEditMode) {
    const selected = workNatures.filter((wn) => selectedWorkNatureIds.has(wn.id));
    if (selected.length === 0 && orphanSelectedIds.length === 0) {
      return <p className="text-gray-500">{t("msgNoWorkNaturesSelected", { ns: NS })}</p>;
    }
    return (
      <div
        className="grid w-full grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
        role="table"
        aria-label={t("tabWorkNatureAssignment", { ns: NS })}
      >
        {selected.map((wn) => {
          const linguisticLabel = getWorkNatureLabel(wn, language);
          const code = wn.workNatureCode?.trim();
          const displayLabel = code && linguisticLabel !== code ? `${linguisticLabel} (${code})` : linguisticLabel;
          return (
            <label key={wn.id} role="cell" className="flex min-w-0 cursor-default items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-800">
              <input type="checkbox" checked disabled readOnly className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 opacity-60" aria-label={displayLabel} />
              <span className="min-w-0 wrap-break-word">{displayLabel}</span>
            </label>
          );
        })}
        {orphanSelectedIds.map((id) => (
          <label key={id} role="cell" className="flex min-w-0 cursor-default items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-500 italic">
            <input type="checkbox" checked disabled readOnly className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 opacity-60" aria-label={t("msgWorkNatureInactiveAssigned", { ns: NS })} />
            <span className="min-w-0 wrap-break-word">{t("msgWorkNatureInactiveAssigned", { ns: NS })}</span>
          </label>
        ))}
      </div>
    );
  }

  return (
    <>
      {workNatures.length === 0 ? (
        <p className="text-gray-500">{t("msgNoWorkNatures", { ns: NS })}</p>
      ) : (
        <>
          {syncHint}
          <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-gray-200 pb-3">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-gray-700">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={handleSelectAllChange}
                className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                aria-label={t("lblSelectAllWorkNatures", { ns: NS })}
                aria-checked={someSelected ? "mixed" : allSelected}
              />
              <span>{t("lblSelectAllWorkNatures", { ns: NS })}</span>
            </label>
          </div>
          <div
            className="grid w-full grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6"
            role="table"
            aria-label={t("tabWorkNatureAssignment", { ns: NS })}
          >
            {workNatures.map((wn) => {
              const linguisticLabel = getWorkNatureLabel(wn, language);
              const code = wn.workNatureCode?.trim();
              const displayLabel = code && linguisticLabel !== code ? `${linguisticLabel} (${code})` : linguisticLabel;
              const checked = selectedWorkNatureIds.has(wn.id);
              return (
                <label key={wn.id} role="cell" className="flex min-w-0 cursor-pointer items-start gap-3 overflow-hidden rounded-sm px-5 py-2.5 text-sm text-gray-800 hover:bg-gray-50">
                  <input
                    type="checkbox"
                    value={wn.id}
                    checked={checked}
                    onChange={(e) => handleWorkNatureChange(wn.id, e.target.checked)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label={displayLabel}
                  />
                  <span className="min-w-0 wrap-break-word">{displayLabel}</span>
                </label>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

export default U3121ProjectWorkNatureAssignmentBody;
